import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function CompetitionManagement() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState(''); // Set initial state to an empty string
    const [Competitions, setCompetitions] = useState([]);
    const [newCompetition, setNewCompetition] = useState({
        CompetitionID: null,
        CompetitionName: '',
        TeamBased: false,
        ScoringType: 'High Score Wins',
        TimeBased: false,
        TimeBasedMultiplier: false,
        AttemptsAllowed: 1,
        RegistrationRequired: false,
        UpNextEnabled: false // Add UpNextEnabled field
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openHelpId, setOpenHelpId] = useState(null);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (selectedEventID) {
            fetchCompetitions(selectedEventID);
            handleCancelEdit(); // Clear the entry values when changing events
        }
    }, [selectedEventID]);

    const fetchCompetitions = async (EventID) => {
        const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${EventID}`);
        const data = await response.json();
        setCompetitions(data);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCompetition(prev => ({ ...prev, [name]: value }));
    };

    const toggleHelp = (key) => {
        setOpenHelpId(prev => (prev === key ? null : key));
    };

    // Add a function to handle checkbox changes
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        
        // If TimeBased is being unchecked, also uncheck TimeBasedMultiplier
        if (name === 'TimeBased' && !checked) {
            setNewCompetition(prev => ({ 
                ...prev, 
                [name]: checked,
                TimeBasedMultiplier: false 
            }));
        } 
        // If TeamBased is being checked, disable UpNextEnabled
        else if (name === 'TeamBased' && checked) {
            setNewCompetition(prev => ({ 
                ...prev, 
                [name]: checked,
                UpNextEnabled: false 
            }));
        }
        else {
            setNewCompetition(prev => ({ ...prev, [name]: checked }));
        }
    };

    const handleAddOrUpdateCompetition = async () => {
        if (!newCompetition.CompetitionName.trim()) {
            alert('Competition name is required!');
            return;
        }
    
        // Check for duplicate names
        const duplicate = Array.isArray(Competitions) && Competitions.some(comp => 
            comp.CompetitionName.trim().toLowerCase() === newCompetition.CompetitionName.trim().toLowerCase() && 
            comp.CompetitionID !== newCompetition.CompetitionID
        );
        if (duplicate) {
            alert('A competition with this name already exists!');
            return;
        }
    
        // Convert ScoringType to HighScoreWins
        const competitionData = {
            ...newCompetition,
            HighScoreWins: newCompetition.ScoringType === 'High Score Wins',
            EventID: selectedEventID
        };
    
        const method = newCompetition.CompetitionID ? 'PUT' : 'POST';
        const endpoint = newCompetition.CompetitionID ? `${API_BASE_URL}/updateCompetition` : `${API_BASE_URL}/createCompetition`;
    
        // Check if TimeBased or TeamBased status has changed
        if (newCompetition.CompetitionID) {
            const originalCompetition = Competitions.find(comp => comp.CompetitionID === newCompetition.CompetitionID);
            if (originalCompetition.TimeBased !== newCompetition.TimeBased) {
                alert('Changing the Time-Based status breaks downstream scoring components and calculations. Sorry, you need to delete and re-create the competition instead.');
                return;
            }
            if (originalCompetition.TeamBased !== newCompetition.TeamBased) {
                alert('Changing the Team-Based status breaks assosciated / linked teams Sorry, you will need to delete and re-create the competition instead.');
                return;
            }
            
            // Check if attempts are being reduced
            if (newCompetition.AttemptsAllowed < originalCompetition.AttemptsAllowed) {
                try {
                    // Get all scores for this competition using the existing API
                    const scoresResponse = await fetch(`${API_BASE_URL}/getScores?EventID=${selectedEventID}&CompetitionID=${newCompetition.CompetitionID}`);
                    
                    // Only proceed with the check if scores were found
                    if (scoresResponse.status === 200) {
                        const scoresData = await scoresResponse.json();
                        
                        // Check if any scores have attempt numbers greater than the new maximum
                        const affectedScores = scoresData.filter(score => score.AttemptNumber > newCompetition.AttemptsAllowed);
                        
                        if (affectedScores.length > 0) {
                            alert(`Cannot reduce attempts to ${newCompetition.AttemptsAllowed}. There are existing scores with affected attempt numbers in this competiton. Please delete these scores first before reducing the allowed attempts.`);
                            return;
                        }
                    }
                } catch (error) {
                    console.error('Error checking affected scores:', error);
                    alert('Error checking for affected scores. Please try again.');
                    return;
                }
            }
        }
    
        const response = await fetch(endpoint, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(competitionData)
        });
    
        if (response.ok) {
            const result = await response.json();
            console.log('Competition created/updated:', result);
    
            // Create scoring components if competition is time-based
            if (newCompetition.TimeBased && !newCompetition.CompetitionID) {
                await fetch(`${API_BASE_URL}/createScoringComponent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        CompetitionID: result.CompetitionID,
                        ScoringComponentName: 'Minutes',
                        Points: 60,
                        ScoringComponentType: 'INT'
                    })
                });
                await fetch(`${API_BASE_URL}/createScoringComponent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        CompetitionID: result.CompetitionID,
                        ScoringComponentName: 'Seconds',
                        Points: 1,
                        ScoringComponentType: 'DECIMAL'
                    })
                });
            }

            // Create scoring component named "Disqualified"
            if (!newCompetition.CompetitionID) {
                await fetch(`${API_BASE_URL}/createScoringComponent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        CompetitionID: result.CompetitionID,
                        ScoringComponentName: 'Disqualified',
                        Points: 1,
                        ScoringComponentType: 'BOOL'
                    })
                });
            }
    
            fetchCompetitions(selectedEventID);
            closeModal();
        } else {
            alert('Error adding/updating competition.');
        }
    };

    const handleDeleteCompetition = async (CompetitionID) => {
        const confirmDelete = window.confirm('!!!WARNING!!! Deleting a competition also deletes any associated Teams, Scores, and scoring components. Are you SURE you want to delete this competition?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/deleteCompetition?CompetitionID=${CompetitionID}`, { method: 'DELETE' });
            if (response.ok) {
                fetchCompetitions(selectedEventID);
            } else {
                alert('Error deleting competition.');
            }
        } catch (error) {
            console.error('Error deleting competition:', error);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewCompetition({
            CompetitionID: null,
            CompetitionName: '',
            TeamBased: false,
            ScoringType: 'High Score Wins',
            TimeBased: false,
            TimeBasedMultiplier: false,
            AttemptsAllowed: 1,
            RegistrationRequired: false,
            UpNextEnabled: false
        });
    };

    const handleEditCompetition = (competition) => {
        setNewCompetition({
            ...competition,
            ScoringType: competition.HighScoreWins ? 'High Score Wins' : 'Low Score Wins'
        });
        openModal();
    };

    const handleCancelEdit = () => {
        setNewCompetition({
            CompetitionID: null,
            CompetitionName: '',
            TeamBased: false,
            ScoringType: 'High Score Wins',
            TimeBased: false,
            TimeBasedMultiplier: false,
            AttemptsAllowed: 1,
            RegistrationRequired: false,
            UpNextEnabled: false // Reset UpNextEnabled field
        });
    };

    return (
        <div className="p-1">
            <div className="flex mb-1">
                <label>
                    <select className="form-select" value={selectedEventID || ''} onChange={(e) => setSelectedEventID(Number(e.target.value))}>
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
            </div>

            <button className="btn btn-primary mb-1" onClick={openModal}>Add Competition</button>

            {Competitions.length > 0 ? (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Team</th>
                                <th>Scoring</th>
                                <th>Time-Based</th>
                                <th>Time-Multiplier</th>
                                <th>Attempts</th>
                                <th>Registration Required</th>
                                <th>Up Next</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Competitions.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center' }}>No Competitions found.</td>
                                </tr>
                            ) : (                    
                                Competitions.map(comp => (
                                    <tr key={comp.CompetitionID}>
                                        <td>{comp.CompetitionName}</td>
                                        <td>{comp.TeamBased ? 'Yes' : 'No'}</td>
                                        <td>{comp.HighScoreWins ? 'High Score Wins' : 'Low Score Wins'}</td>
                                        <td>{comp.TimeBased ? 'Yes' : 'No'}</td>
                                        <td>{comp.TimeBasedMultiplier ? 'Yes' : 'No'}</td>
                                        <td>{comp.AttemptsAllowed || 1}</td>
                                        <td>{!comp.TeamBased && comp.RegistrationRequired ? 'Yes' : 'No'}</td>
                                        <td>{!comp.TeamBased && comp.UpNextEnabled ? 'Yes' : 'No'}</td>
                                        <td>
                                            {comp.ScoringComponentName !== 'Time' && (
                                                <>
                                                    <button className="btn btn-primary" onClick={() => handleEditCompetition(comp)}>Edit</button>
                                                    <button className="btn btn-danger" onClick={() => handleDeleteCompetition(comp.CompetitionID)} style={{ marginLeft: '0.5rem' }}>Delete</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>No Competitions found for this event.</p>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{newCompetition.CompetitionID ? 'Edit Competition' : 'Add Competition'}</h3>
                        <input
                            className="form-control"
                            type="text"
                            name="CompetitionName"
                            placeholder="Name"
                            value={newCompetition.CompetitionName}
                            onChange={handleInputChange}
                        />
                        <div className="flex items-center mb-1">
                            <label className="form-checkbox">
                                Team Based:
                                <input
                                    type="checkbox"
                                    name="TeamBased"
                                    checked={newCompetition.TeamBased}
                                    onChange={handleCheckboxChange} 
                                />
                            </label>
                        </div>
                        
                        <div className="flex items-center mb-1">
                            <label className={`form-checkbox ${newCompetition.TeamBased ? 'opacity-50' : ''}`}>
                                Registration Required:
                                <input
                                    type="checkbox"
                                    name="RegistrationRequired"
                                    checked={newCompetition.RegistrationRequired}
                                    onChange={handleCheckboxChange}
                                    disabled={newCompetition.TeamBased}
                                />
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('registrationRequired')}
                                    aria-label="Registration required help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'registrationRequired' && (
                                    <span className="help-text">
                                        {newCompetition.TeamBased
                                            ? 'If checked, competitors must be registered for this competition to be scored. Only applicable to Individual competitions.'
                                            : 'If checked, competitors must be registered for this competition to be scored.'}
                                    </span>
                                )}
                            </label>
                        </div>

                        <div className="flex items-center mb-1">
                            <label className={`form-checkbox ${newCompetition.TeamBased ? 'opacity-50' : ''}`}>
                                Up Next Enabled:
                                <input
                                    type="checkbox"
                                    name="UpNextEnabled"
                                    checked={newCompetition.UpNextEnabled}
                                    onChange={handleCheckboxChange}
                                    disabled={newCompetition.TeamBased}
                                />
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('upNextEnabled')}
                                    aria-label="Up next enabled help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'upNextEnabled' && (
                                    <span className="help-text">
                                        {newCompetition.TeamBased
                                            ? 'Displays a listing of which competitors have been scored and who is coming up next in the competition. Only applicable to Individual competitions.'
                                            : 'Displays a listing of which competitors have been scored and who is coming up next in the competition.'}
                                    </span>
                                )}
                            </label>
                        </div>

                        <select 
                            className="form-select"
                            name="ScoringType" 
                            value={newCompetition.ScoringType} 
                            onChange={handleInputChange}
                        >
                            <option>High Score Wins</option>
                            <option>Low Score Wins</option>
                        </select>
                        <div className="flex items-center mb-1">
                            <label className="form-checkbox">
                                Time Based:
                                <input
                                    type="checkbox"
                                    name="TimeBased"
                                    checked={newCompetition.TimeBased}
                                    onChange={handleCheckboxChange}
                                />
                            </label>
                        </div>
                        {newCompetition.TimeBased && newCompetition.ScoringType === 'High Score Wins' && (
                            <div className="text-red-600 mb-1">
                                Are you sure? Typically time components are only associated with Low Score Wins competitions
                            </div>
                        )}
                        <div className="flex items-center mb-1">
                            <label className={`form-checkbox ${!newCompetition.TimeBased ? 'opacity-50' : ''}`}>
                                Time Multiplier:
                                <input
                                    type="checkbox"
                                    name="TimeBasedMultiplier"
                                    checked={newCompetition.TimeBasedMultiplier}
                                    onChange={handleCheckboxChange}
                                    disabled={!newCompetition.TimeBased}
                                />
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('timeMultiplier')}
                                    aria-label="Time multiplier help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'timeMultiplier' && (
                                    <span className="help-text">
                                        Multiplies (elapsed seconds x (minutes + 1)). Only applies to time based competitions.
                                    </span>
                                )}
                            </label>
                        </div>
                        <div className="flex items-center mb-1">
                            <label>
                                Attempts Allowed:
                                <input
                                    className="form-control ml-1"
                                    type="number"
                                    name="AttemptsAllowed"
                                    min="1"
                                    value={newCompetition.AttemptsAllowed}
                                    onChange={handleInputChange}
                                    style={{ width: '80px' }}
                                />
                            </label>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateCompetition}>
                                {newCompetition.CompetitionID ? 'Update Competition' : 'Add Competition'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}